import { cn } from "@/lib/cn";

/**
 * Workshop: Activity feed item — timestamp, colored dot, optional source badge, and description.
 *
 * Source badge shows why the activity happened: Chat, Heartbeat, Event, Scheduled.
 */

type ActivitySource = "chat" | "heartbeat" | "event" | "scheduled" | "comment" | "user";

const SOURCE_STYLES: Record<ActivitySource, { label: string; color: string }> = {
  chat: { label: "Chat", color: "text-accent" },
  heartbeat: { label: "Heartbeat", color: "text-muted" },
  event: { label: "Event", color: "text-warning" },
  scheduled: { label: "Scheduled", color: "text-secondary" },
  comment: { label: "Comment", color: "text-accent" },
  user: { label: "User", color: "text-secondary" },
};

interface ActivityItemProps {
  timestamp: string;
  description: string;
  dotColor?: string;
  /** Source of the activity — shown as a small badge */
  source?: ActivitySource;
  className?: string;
  "data-testid"?: string;
}

export function ActivityItem({
  timestamp,
  description,
  dotColor = "var(--color-muted)",
  source,
  className,
  "data-testid": testId,
}: ActivityItemProps) {
  return (
    <div
      className={cn("flex items-start gap-2 py-1", className)}
      data-testid={testId}
    >
      <span className="w-10 shrink-0 text-[10px] text-muted">
        {timestamp}
      </span>
      <span
        className="mt-[3px] h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: dotColor }}
        aria-hidden="true"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {source && (
          <span className={cn("text-[9px] font-medium", SOURCE_STYLES[source].color)}>
            {SOURCE_STYLES[source].label}
          </span>
        )}
        <span className="text-xs text-secondary">{description}</span>
      </div>
    </div>
  );
}

export type { ActivityItemProps, ActivitySource };
